// src/pages/auth/Login.jsx
'use client';

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { auth } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { getInviteByCode } from '@/services/inviteService';
import AuthShell from '@/components/auth/AuthShell';

const STORAGE_KEY = 'pendingInviteCode';
const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';
const ENABLE_SOCIAL = import.meta?.env?.VITE_ENABLE_SOCIAL === 'true';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INVITE_CODE_RE = /^[A-Za-z0-9_-]{4,64}$/;

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function ErrorBanner({ message, onClose }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="mb-5 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
    >
      <span className="mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-red-400" />
      <div className="flex-1">{message}</div>
      <button
        type="button"
        onClick={onClose}
        className="rounded p-1 text-red-300 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-red-400"
        aria-label="Dismiss error"
      >
        ×
      </button>
    </div>
  );
}

function sanitizeRedirect(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null;
  if (/[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  if (/[\s\x00-\x1f]/.test(trimmed)) return null;
  return trimmed;
}

function buildFromLocation(loc) {
  if (!loc) return null;
  if (typeof loc === 'string') {
    const s = loc.trim();
    return s ? s : null;
  }
  const pathname = typeof loc.pathname === 'string' ? loc.pathname : '';
  if (!pathname) return null;
  const search = typeof loc.search === 'string' ? loc.search : '';
  const hash = typeof loc.hash === 'string' ? loc.hash : '';
  return `${pathname}${search}${hash}`;
}

function safeReadSession(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeRemoveSession(key) {
  try { sessionStorage.removeItem(key); } catch {}
}

export default function Login() {
  const { login, loadingAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteCode = (searchParams.get('invite') || '').trim() || null;

  const fromState = useMemo(
    () => sanitizeRedirect(buildFromLocation(location.state?.from)),
    [location.state]
  );
  const fromSession = useMemo(
    () => sanitizeRedirect(safeReadSession(POST_LOGIN_REDIRECT_KEY)),
    []
  );

  // Persist the invite code so the post-login redirect at line ~134 can
  // bounce back to /invite/{code} for the auto-claim, and fetch the invite
  // doc so the hero can name the team the user is being added to.
  const [inviteCtx, setInviteCtx] = useState(null);
  useEffect(() => {
    if (!inviteCode) return;
    try { sessionStorage.setItem(STORAGE_KEY, inviteCode); } catch {}
    let cancelled = false;
    (async () => {
      try {
        const data = await getInviteByCode(inviteCode);
        if (!cancelled && data) setInviteCtx(data);
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[Login] invite fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [inviteCode]);

  const isParentInvite = String(inviteCtx?.audience || '').toLowerCase() === 'parent';
  const hasInviteCtx = Boolean(inviteCtx);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // Beta-gate banner — set in AuthContext when a non-allowlisted user
  // had a session forcibly closed. Read once on mount and clear so it
  // doesn't follow them around if they navigate away and come back.
  const [betaGateBlocked, setBetaGateBlocked] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem('scrimly.betaGate.blocked') === '1') {
        setBetaGateBlocked(true);
        sessionStorage.removeItem('scrimly.betaGate.blocked');
      }
    } catch {}
  }, []);

  const isLoading = isSubmitting || loadingAuth;
  const emailValid = EMAIL_RE.test(email);
  const canSubmit = emailValid && !!password && !isLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    if (!emailValid) {
      setLocalError('Please enter a valid email address.');
      return;
    }
    if (isLoading) return;

    setLocalError(null);
    setIsSubmitting(true);

    try {
      try {
        await setPersistence(
          auth,
          rememberMe ? browserLocalPersistence : browserSessionPersistence
        );
      } catch (persistErr) {
        console.warn('Could not set auth persistence:', persistErr);
      }

      await login(email, password);

      if (fromSession) safeRemoveSession(POST_LOGIN_REDIRECT_KEY);

      let pendingInvite = null;
      try {
        pendingInvite = sessionStorage.getItem(STORAGE_KEY);
        if (pendingInvite) sessionStorage.removeItem(STORAGE_KEY);
      } catch {}

      if (pendingInvite && INVITE_CODE_RE.test(pendingInvite)) {
        navigate(`/invite/${encodeURIComponent(pendingInvite)}`, { replace: true });
        return;
      }

      const params = new URLSearchParams(location.search);
      const next = sanitizeRedirect(params.get('next'));
      if (next) {
        navigate(next, { replace: true });
        return;
      }

      const fallbackTo = fromState || fromSession || '/dashboard';
      navigate(fallbackTo, { replace: true });
    } catch (err) {
      console.error('Login error:', err);

      const rawCode = typeof err?.code === 'string' ? err.code : '';
      const code = rawCode.toLowerCase();

      let friendly;
      if (code === 'auth/beta-closed') {
        // Beta gate threw — show the persistent banner instead of an
        // error toast so the messaging is informational, not "something
        // went wrong with your password."
        setBetaGateBlocked(true);
        setIsSubmitting(false);
        return;
      } else if (
        code === 'auth/invalid-credential' ||
        code === 'auth/wrong-password' ||
        code === 'auth/invalid-email' ||
        code === 'auth/user-not-found'
      ) {
        friendly = 'Invalid email or password. Double-check and try again.';
      } else if (code === 'auth/user-disabled') {
        friendly = 'This account has been disabled. Contact support if this seems wrong.';
      } else if (code === 'auth/too-many-requests') {
        friendly = 'Too many sign-in attempts. Please wait a bit and try again.';
      } else if (code === 'auth/network-request-failed') {
        friendly = 'Unable to reach the server. Check your internet connection and try again.';
      } else {
        friendly = 'We could not sign you in. Please try again.';
      }
      setLocalError(friendly);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full min-h-[52px] rounded-[10px] border border-white/10 bg-white/5 px-4 py-3.5 text-[15px] text-white placeholder:text-white/30 transition-colors focus:border-[#b5f03d] focus:bg-white/[0.08] focus:outline-none disabled:opacity-60';

  return (
    <AuthShell>
            <div className="text-center">
              <span
                className="inline-flex items-center rounded-full border border-[#b5f03d]/30 bg-[#b5f03d]/10 px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.5px] text-[#b5f03d]"
              >
                {hasInviteCtx ? "You're invited" : 'Sign in'}
              </span>

              {hasInviteCtx ? (
                <>
                  <h1
                    className="mt-5 uppercase leading-none text-white"
                    style={{
                      fontFamily: HEAD_FONT,
                      fontWeight: 900,
                      fontSize: 'clamp(28px, 3.6vw, 40px)',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    Sign in to join{' '}
                    <span className="text-[#b5f03d]">
                      {inviteCtx?.teamName || 'the team'}
                    </span>
                  </h1>

                  <p className="mt-3.5 text-[15.5px] leading-relaxed text-white/65">
                    {isParentInvite
                      ? 'Sign in with your existing Scrimly account to connect to your child\u2019s team.'
                      : 'Sign in to your existing Scrimly account to accept this invite.'}
                  </p>
                </>
              ) : (
                <>
                  <h1
                    className="mt-5 uppercase leading-none text-white"
                    style={{
                      fontFamily: HEAD_FONT,
                      fontWeight: 900,
                      fontSize: 'clamp(32px, 4vw, 44px)',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    Welcome
                    <br />
                    <span className="text-[#b5f03d] not-italic">back.</span>
                  </h1>

                  <p className="mt-3.5 text-[15.5px] leading-relaxed text-white/65">
                    Sign in to your Scrimly account.
                  </p>
                </>
              )}
            </div>

            <div className="mt-7">
              {/* Private beta gate — shown both when a user was just
                  bounced (sessionStorage flag set by AuthContext) and
                  as standing context so first-time visitors understand
                  why their account doesn't work. Lime/pitch styling so
                  it reads as informational, not error. */}
              {betaGateBlocked && (
                <div
                  role="status"
                  className="mb-5 flex items-start gap-3 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-4 py-3 text-sm text-[#b5f03d]"
                >
                  <span className="mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[#b5f03d]" />
                  <div className="flex-1">
                    <p className="font-bold mb-1" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                      PRIVATE BETA — NOT OPEN YET
                    </p>
                    <p className="text-[#b5f03d]/90">
                      Scrimly isn't open to the public yet. If you'd like access, email{' '}
                      <a
                        href="mailto:hello@getscrimly.com"
                        className="underline underline-offset-2 hover:text-white"
                      >
                        hello@getscrimly.com
                      </a>
                      .
                    </p>
                  </div>
                </div>
              )}

              <ErrorBanner message={localError} onClose={() => setLocalError(null)} />

              {ENABLE_SOCIAL && (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      disabled
                      title="Coming soon"
                      className="rounded-[10px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/70 disabled:cursor-not-allowed"
                    >
                      Continue with Google
                    </button>
                    <button
                      type="button"
                      disabled
                      title="Coming soon"
                      className="rounded-[10px] border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/70 disabled:cursor-not-allowed"
                    >
                      Continue with Apple
                    </button>
                  </div>
                  <div className="my-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[1.5px] text-white/40">
                    <div className="h-px flex-1 bg-white/10" />
                    or
                    <div className="h-px flex-1 bg-white/10" />
                  </div>
                </>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 text-left" noValidate>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="email"
                    className="text-[13px] font-bold uppercase tracking-[1.2px] text-white/80"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (localError) setLocalError(null);
                    }}
                    required
                    autoFocus
                    aria-invalid={!!localError || (email.length > 0 && !emailValid)}
                    disabled={isLoading}
                    autoComplete="email"
                    placeholder="coach@yourteam.com"
                    className={inputClass}
                  />
                  {email.length > 0 && !emailValid && !localError && (
                    <p className="mt-1 text-xs text-red-300">
                      Please enter a valid email address.
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="password"
                      className="text-[13px] font-bold uppercase tracking-[1.2px] text-white/80"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      Password
                    </label>
                    <RouterLink
                      to="/forgot-password"
                      className="text-xs font-semibold text-[#b5f03d] hover:underline"
                    >
                      Forgot password?
                    </RouterLink>
                  </div>

                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (localError) setLocalError(null);
                      }}
                      required
                      aria-invalid={!!localError}
                      aria-describedby={localError ? 'login-error' : undefined}
                      disabled={isLoading}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className={`${inputClass} pr-16`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-1 my-1 rounded-md px-3 text-xs font-semibold text-white/70 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-[#b5f03d]"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                <label className="flex cursor-pointer select-none items-center gap-2 pt-1 text-sm text-white/70">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                    className="h-4 w-4 cursor-pointer rounded border-white/20 bg-white/10 text-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]"
                  />
                  Keep me signed in
                </label>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  aria-busy={isLoading}
                  className="mt-2 inline-flex w-full min-h-[52px] items-center justify-center rounded-[10px] bg-[#b5f03d] px-6 py-4 uppercase tracking-[0.5px] text-[#0d1f14] transition-all hover:bg-[#94cc28] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(181,240,61,0.25)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 18 }}
                >
                  {isLoading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>

              {/* Passwordless sign-in alternative. Wired to MagicLinkLogin
                  which sends a one-tap link to the user's email. Useful
                  for users who forget their password OR who never set
                  one (older invite-only signups). */}
              <div className="mt-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[1.5px] text-white/35">
                <div className="h-px flex-1 bg-white/10" />
                or
                <div className="h-px flex-1 bg-white/10" />
              </div>
              <RouterLink
                to="/auth/magic-link-login"
                className="mt-3 inline-flex w-full min-h-[48px] items-center justify-center rounded-[10px] border border-white/15 bg-white/5 px-4 py-3 text-sm font-bold uppercase text-white hover:bg-white/10 transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                Email me a sign-in link
              </RouterLink>

              <p className="mt-6 text-center text-sm text-white/55">
                New to Scrimly?{' '}
                <RouterLink
                  to={inviteCode ? `/register?invite=${encodeURIComponent(inviteCode)}` : '/register'}
                  className="font-semibold text-[#b5f03d] hover:underline"
                >
                  Create a free account
                </RouterLink>
              </p>
              <p className="mt-2 text-center text-xs text-white/40">
                Curious what it costs?{' '}
                <RouterLink
                  to="/pricing"
                  className="font-semibold text-[#b5f03d]/80 hover:text-[#b5f03d] hover:underline"
                >
                  See pricing
                </RouterLink>
              </p>
            </div>
    </AuthShell>
  );
}
